import {
  CSSProperties,
  Dispatch,
  forwardRef,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { Button } from '@hhgtech/hhg-components'
import { Props as ButtonProps } from '@hhgtech/hhg-components/build/components/atoms/button'

import { ExtendingDataProps } from '../interfaces/types'
import { StyledPopoverMenu } from './index.styled'

export type Props<CustomDataProps = unknown> = {
  className?: string
  style?: CSSProperties
  toggleButtonContent?: ReactNode
  children?:
    | ((data: { setShow?: Dispatch<SetStateAction<boolean>> }) => ReactNode)
    | ReactNode
    | null
  position?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'end'
  buttonProps?: Omit<ButtonProps, 'children' | 'size'> & {
    size?: ButtonProps['size']
  }
  contentDataProps?: ExtendingDataProps<CustomDataProps>
  onChange?: (state: boolean) => void
}

export const PopoverMenu = forwardRef(function PopoverMenu(
  {
    className,
    style,
    toggleButtonContent,
    position = 'bottom',
    align = 'end',
    children,
    buttonProps,
    contentDataProps,
    onChange,
    ...rest
  }: Props,
  ref,
) {
  const [isShow, setIsShow] = useState(false)
  const popoverContent = useRef<HTMLDivElement>(null)
  const popoverBtn = useRef<HTMLDivElement>(null)

  const isShowRef = useRef(isShow)

  const handleSetShow = useCallback((state) => setIsShow(state), [])

  useImperativeHandle(ref, () => ({
    setShow: handleSetShow,
  }))

  useEffect(() => {
    onChange?.(isShow)
    isShowRef.current = isShow
    if (!isShow) return
    popoverContent.current?.focus()
  }, [isShow])

  useEffect(() => {
    const clickHandler = (e: MouseEvent) => {
      if (
        !popoverContent.current?.contains(e.target as Node) &&
        !popoverBtn.current?.contains(e.target as Node) &&
        isShowRef.current
      )
        setIsShow(false)
    }
    window.addEventListener('click', clickHandler)
    return () => {
      window.removeEventListener('click', clickHandler)
    }
  }, [])

  return (
    <StyledPopoverMenu
      className={className}
      style={style}
      onClick={() => setIsShow((v) => !v)}
      {...rest}
    >
      <div ref={popoverBtn}>
        <Button
          className="popover-menu__btn"
          size="md"
          color="transparent"
          // onClick={() => setIsShow((v) => !v)}
          {...buttonProps}
        >
          {toggleButtonContent}
        </Button>
      </div>

      {isShow ? (
        <div
          ref={popoverContent}
          className={`popover-menu__content --position-${position} --align-${align}`}
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          {...contentDataProps}
        >
          {typeof children === 'function'
            ? children({
                setShow: setIsShow,
              })
            : children}
        </div>
      ) : null}
    </StyledPopoverMenu>
  )
})
